import { useState, useEffect, useCallback } from 'react';
import { PedidoGroup } from '../../../Configuracion/types';
import {
  createPedidoGroup,
  getActivePedidoGroupsByLocalId,
  updatePedidoGroupName,
  deactivatePedidoGroup,
  deletePedidoGroup,
} from '../../../../../crud/pedidos_api/pedido_groups';
import { toast } from 'react-toastify';

interface UsePedidoGroupsReturn {
  pedidoGroups: PedidoGroup[];
  loading: boolean;
  creating: boolean;
  newlyCreatedGroupId: number | null;
  handleCreateGroup: () => Promise<void>;
  handleUpdateGroupName: (groupId: number, newName: string) => Promise<void>;
  handleDeactivateGroup: (groupId: number) => Promise<void>;
  handleDeleteGroup: (groupId: number) => Promise<void>;
  refreshGroups: () => Promise<void>;
  clearNewlyCreatedFlag: () => void;
}

interface UsePedidoGroupsOptions {
  enabled?: boolean; // Control whether to fetch data on mount
}

export const usePedidoGroups = (options: UsePedidoGroupsOptions = {}): UsePedidoGroupsReturn => {
  const { enabled = true } = options;
  const [pedidoGroups, setPedidoGroups] = useState<PedidoGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newlyCreatedGroupId, setNewlyCreatedGroupId] = useState<number | null>(null);

  // Fetch active pedido groups for the current local
  const refreshGroups = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id;

    if (!localId) {
      console.error('No se pudo obtener el ID del local');
      setLoading(false); // Ensure loading is false even if no localId
      return;
    }

    setLoading(true);
    
    // Safety timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Groups fetch timeout - resetting loading state');
      setLoading(false);
    }, 10000); // 10 second timeout
    
    try {
      const response = await getActivePedidoGroupsByLocalId(localId);
      clearTimeout(timeoutId);
      setPedidoGroups(response.data || []);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error fetching pedido groups:', error);
      toast.error('Error cargando grupos de pedidos');
      setPedidoGroups([]); // Set empty array on error to prevent infinite loading
    } finally {
      setLoading(false);
    }
  }, []);

  // Load groups on mount only if enabled
  useEffect(() => {
    if (enabled) {
      refreshGroups();
    }
  }, [refreshGroups, enabled]);

  // Listen for group deactivation events to refresh the list
  useEffect(() => {
    let isActive = true; // Flag to prevent state updates after unmount

    const handleGroupDeactivated = () => {
      // Add a small delay to ensure the deactivation has been processed
      setTimeout(() => {
        if (isActive) {
          refreshGroups();
        }
      }, 300);
    };

    const handleGroupReset = () => {
      // Add a small delay to ensure the reset has been processed
      setTimeout(() => {
        if (isActive) {
          refreshGroups();
        }
      }, 300);
    };

    window.addEventListener('pedido-group-deactivated', handleGroupDeactivated);
    window.addEventListener('pedido-group-reset', handleGroupReset);
    
    return () => {
      isActive = false;
      window.removeEventListener('pedido-group-deactivated', handleGroupDeactivated);
      window.removeEventListener('pedido-group-reset', handleGroupReset);
    };
  }, [refreshGroups]);

  // Create a new pedido group
  const handleCreateGroup = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id;

    if (!localId) {
      toast.error('No se pudo obtener el ID del local');
      return;
    }

    setCreating(true);
    try {
      const response = await createPedidoGroup(localId);
      const newGroup = (response as any).data;
      
      // Append the new group to the list
      setPedidoGroups((prev) => [...prev, newGroup]);
      
      // Mark this group as newly created (will show delete button)
      setNewlyCreatedGroupId(newGroup.pedido_group_id);
      
      toast.success(`Grupo ${newGroup.name} creado exitosamente`);
    } catch (error) {
      console.error('Error creating pedido group:', error);
      toast.error('Error creando grupo de pedidos');
    } finally {
      setCreating(false);
    }
  }, []);

  // Update a group's name
  const handleUpdateGroupName = useCallback(async (groupId: number, newName: string) => {
    try {
      const response = await updatePedidoGroupName(groupId, newName);
      const updatedGroup = (response as any).data;
      
      // Update the group in the list
      setPedidoGroups((prev) =>
        prev.map((group) =>
          group.pedido_group_id === groupId ? updatedGroup : group
        )
      );
      toast.success('Nombre del grupo actualizado');
    } catch (error) {
      console.error('Error updating group name:', error);
      toast.error('Error actualizando nombre del grupo');
    }
  }, []);

  // Deactivate a group
  const handleDeactivateGroup = useCallback(async (groupId: number) => {
    try {
      await deactivatePedidoGroup(groupId);
      
      // Remove the group from the list
      setPedidoGroups((prev) =>
        prev.filter((group) => group.pedido_group_id !== groupId)
      );
      toast.success('Grupo desactivado exitosamente');
    } catch (error) {
      console.error('Error deactivating group:', error);
      toast.error('Error desactivando grupo');
    }
  }, []);

  // Delete a group (only works for empty groups)
  const handleDeleteGroup = useCallback(async (groupId: number) => {
    try {
      await deletePedidoGroup(groupId);
      
      // Remove the group from the list
      setPedidoGroups((prev) =>
        prev.filter((group) => group.pedido_group_id !== groupId)
      );
      
      // Clear the newly created flag if this was the newly created group
      if (newlyCreatedGroupId === groupId) {
        setNewlyCreatedGroupId(null);
      }
      
      toast.success('Grupo eliminado exitosamente');
    } catch (error: any) {
      console.error('Error deleting group:', error);
      // Show specific error message from backend if available
      const errorMessage = error?.response?.data?.message || 'Error eliminando grupo';
      toast.error(errorMessage);
    }
  }, [newlyCreatedGroupId]);

  // Clear the newly created flag
  const clearNewlyCreatedFlag = useCallback(() => {
    setNewlyCreatedGroupId(null);
  }, []);

  return {
    pedidoGroups,
    loading,
    creating,
    newlyCreatedGroupId,
    handleCreateGroup,
    handleUpdateGroupName,
    handleDeactivateGroup,
    handleDeleteGroup,
    refreshGroups,
    clearNewlyCreatedFlag,
  };
};
